import React, { useState } from "react";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { useTranslation } from "locales";
import useOfflineMode from "hooks/useOfflineMode";
import BaseAvatar from "components/BaseAvatar";
import BaseIcon from "components/BaseIcon";
import UserProfileModal from "components/UserProfileModal";
import MediaGalleryPanel from "components/MediaGalleryPanel";
import "./style.scss";

interface TypingUser {
  userId: string;
  userName: string;
}

interface Props {
  user: UserPublicData | null;
  threadId?: string;
  onBack?: () => void;
  showBackButton?: boolean;
  onToggleSearch?: () => void;
  searchOpen?: boolean;
  typingUsers?: TypingUser[];
  onVideoCall?: () => void;
  onVoiceCall?: () => void;
  onScrollToMessage?: (messageId: string) => void;
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path
        d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
        fill="currentColor"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" fill="currentColor" />
    </svg>
  );
}

function VideoCamIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" fill="currentColor" />
    </svg>
  );
}

function MediaIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" fill="currentColor" />
    </svg>
  );
}

function ChatHeader({ user, threadId, onBack, showBackButton, onToggleSearch, searchOpen, typingUsers, onVideoCall, onVoiceCall, onScrollToMessage }: Props) {
  const { t } = useTranslation();
  const [showProfile, setShowProfile] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const { isOffline, isReconnecting } = useOfflineMode();

  if (!user) return null;

  const isOnline = user.onlineStatus?.isOnline;
  const hasTyping = typingUsers && typingUsers.length > 0;
  const statusCls = ["chat-header__status"];
  if (isOffline) statusCls.push("__offline-mode");
  else if (isReconnecting) statusCls.push("__offline-mode");
  else if (isOnline && !hasTyping) statusCls.push("__online");
  if (hasTyping) statusCls.push("__typing");

  const statusText = isOffline
    ? t("Waiting for network...")
    : isReconnecting
      ? t("Reconnecting...")
      : hasTyping
        ? t("typing...")
        : isOnline
          ? t("Online")
          : t("Offline");

  return (
    <div className="chat-header">
      {showBackButton && (
        <button className="chat-header__back-btn" onClick={onBack} type="button">
          <BaseIcon>back</BaseIcon>
        </button>
      )}
      <div
        className="chat-header__user-area"
        onClick={() => setShowProfile(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setShowProfile(true))}
      >
        <BaseAvatar user={user} size={42} className="chat-header__avatar" />
        <div className="chat-header__info">
          <div className="chat-header__name">{user.screenName}</div>
          <div className={statusCls.join(" ")}>{statusText}</div>
        </div>
      </div>
      {onToggleSearch && (
        <button
          className={`chat-header__search-btn${searchOpen ? " __active" : ""}`}
          onClick={onToggleSearch}
          type="button"
          title={t("Search messages")}
        >
          <SearchIcon />
        </button>
      )}
      {onVoiceCall && (
        <button
          className="chat-header__call-btn"
          onClick={onVoiceCall}
          type="button"
          title={t("Voice call")}
        >
          <PhoneIcon />
        </button>
      )}
      {onVideoCall && (
        <button
          className="chat-header__call-btn"
          onClick={onVideoCall}
          type="button"
          title={t("Video call")}
        >
          <VideoCamIcon />
        </button>
      )}
      {threadId && (
        <div className="chat-header__media-container">
          <button
            className={`chat-header__call-btn${showMedia ? " __active" : ""}`}
            onClick={() => setShowMedia((v) => !v)}
            type="button"
            title={t("Shared media")}
          >
            <MediaIcon />
          </button>
          {showMedia && (
            <div className="chat-header__media-panel">
              <MediaGalleryPanel threadId={threadId} onClose={() => setShowMedia(false)} onScrollToMessage={onScrollToMessage} />
            </div>
          )}
        </div>
      )}
      {showProfile && <UserProfileModal userId={String(user.id)} onClose={() => setShowProfile(false)} />}
    </div>
  );
}

export default ChatHeader;
